import type { Role } from './policy';

/**
 * Reading the access token's claims, for the interface's benefit only
 * (docs/07-implementation-roadmap.md F1.2).
 *
 * **Nothing here is verified and nothing here is trusted.** The signature is
 * checked by the API on every request; this decode exists so the shell can draw
 * the right sidebar before the first response comes back, and so a coordinator's
 * own region is available to `policy.ts` without an extra round-trip. Every
 * decision that matters is the server's — a forged token changes what this app
 * *draws*, and changes nothing about what it may *do*.
 *
 * Claims are per docs/04 §1.1: `sub`, `churchId`, `role`, `memberId`,
 * `regionId`, plus the standard `exp`.
 */

export interface AccessTokenClaims {
  sub: string;
  churchId: string;
  role: Role;
  memberId: string | null;
  regionId: string | null;
  /** Seconds since the epoch, as JWTs count. Absent if the token carries no `exp`. */
  exp?: number;
}

const ROLES: readonly string[] = ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR', 'MEMBER'];

/** base64url → UTF-8. `atob` handles neither the alphabet nor multi-byte characters on its own. */
function decodeSegment(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * The claims, or `null` if the token is not a JWT this app can read.
 *
 * A token that fails to decode is treated as no session at all rather than as
 * an error to show: the only ways to get one are a bug or tampering, and both
 * are better answered by the login screen than by a stack trace.
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(decodeSegment(segments[1]));
  } catch {
    return null;
  }

  if (!payload || typeof payload !== 'object') return null;
  const claims = payload as Record<string, unknown>;

  if (typeof claims.sub !== 'string' || typeof claims.churchId !== 'string') return null;
  if (typeof claims.role !== 'string' || !ROLES.includes(claims.role)) return null;

  return {
    sub: claims.sub,
    churchId: claims.churchId,
    role: claims.role as Role,
    memberId: typeof claims.memberId === 'string' ? claims.memberId : null,
    regionId: typeof claims.regionId === 'string' ? claims.regionId : null,
    exp: typeof claims.exp === 'number' ? claims.exp : undefined,
  };
}

/** When this token stops being accepted, in epoch milliseconds. `null` if it never says. */
export function expiresAt(claims: AccessTokenClaims): number | null {
  return claims.exp === undefined ? null : claims.exp * 1000;
}
